import { CSRF_HEADER, readCsrfToken, setCsrfToken } from "./csrf";

// Capture the token the server exposes on every response. Cross-origin
// (split-run dev) this is the ONLY way the SPA learns the token, since the
// API-origin studio_csrf cookie isn't readable via document.cookie.
function captureCsrf(res: Response): void {
  const token = res.headers.get(CSRF_HEADER);
  if (token) setCsrfToken(token);
}

// Injected at build time from process.env.STUDIO_API_URL. Empty in production
// (server serves the SPA at same origin). Set in split-run dev so browser
// requests go directly to the studio server instead of the page origin.
declare const __STUDIO_API_BASE__: string;
export const API_BASE: string = typeof __STUDIO_API_BASE__ !== "undefined" ? __STUDIO_API_BASE__ : "";

// Single concrete shape for everything the API throws so React Query
// callbacks have a typed `Error` rather than `unknown`. `code` mirrors
// the server's error.code enum (spec §"Error handling").
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// One callback the router installs at boot. Kept as a mutable export so
// `apiFetch` (called from anywhere — including outside React) can
// trigger a redirect without importing the router.
let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn;
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface ApiFetchOptions {
  readonly method?: Method;
  readonly body?: unknown;            // JSON-serialized automatically
  readonly signal?: AbortSignal;
  readonly headers?: Record<string, string>;
}

export async function apiFetch<T>(path: string, opts: ApiFetchOptions = {}): Promise<T> {
  const method: Method = opts.method ?? "GET";
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.headers ?? {}),
  };
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  if (method !== "GET") {
    let token = readCsrfToken();
    if (!token) {
      // Cold load straight to a form (e.g. a bookmarked /sign-in) means no
      // safe request has set the studio_csrf cookie yet, so the very first
      // submit would 403. Bootstrap it with one safe GET, then re-read. Capture
      // the exposed token header too — the only source cross-origin.
      const boot = await fetch(API_BASE + "/api/me", { credentials: "include" }).catch(
        () => undefined,
      );
      if (boot) captureCsrf(boot);
      token = readCsrfToken();
    }
    if (token) headers[CSRF_HEADER] = token;
  }
  let res: Response;
  try {
    res = await fetch(API_BASE + path, {
      method,
      headers,
      body,
      credentials: "include",
      signal: opts.signal,
    });
  } catch (err) {
    // A dropped connection makes fetch reject with a TypeError (never an
    // ApiError). Preserve react-query's abort signal; map everything else to a
    // `network` code so the UI can localise it.
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError(0, "network", "Network error. Check your connection.", undefined);
  }
  // Refresh the cached CSRF token from the response (cross-origin source).
  captureCsrf(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const parsed: unknown = text ? safeJson(text) : null;
  if (!res.ok) {
    const envelope = parsed && typeof parsed === "object" && "error" in parsed
      ? (parsed as { error: { code?: string; message?: string; details?: unknown } }).error
      : null;
    // A 401 on a normal request means the session is gone → let the router
    // bounce to /sign-in. But the auth endpoints return 401 for bad
    // credentials, which the form must surface inline with the SERVER's message
    // ("Invalid email or password") — not a generic "Not signed in" + redirect.
    const isAuthAttempt = path.includes("/auth/sign-in") || path.includes("/auth/sign-up");
    if (res.status === 401 && !isAuthAttempt) unauthorizedHandler?.();
    throw new ApiError(
      res.status,
      envelope?.code ?? (res.status === 401 ? "unauthenticated" : "internal"),
      envelope?.message ?? (res.status === 401 ? "Not signed in" : `Request failed (${res.status})`),
      envelope?.details,
    );
  }
  return parsed as T;
}

function safeJson(text: string): unknown {
  try { return JSON.parse(text); } catch { return null; }
}
